import React, { Fragment, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { history } from '../../';

import { ThemeProvider } from '@material-ui/styles';
import { isWidthDown } from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

import Skeleton from '@material-ui/lab/Skeleton';

import { tabRoutes } from '../../routes';
import { DIMENSIONS } from '../../styles/constants';
import { useStyles } from './styles';
import theme from './theme';
import SidepanelLayout from '../SidepanelLayout';
import ErrorPage from '../ErrorPage';
import Paper from '../../components/Paper';
import InputParameters from '../../components/InputParameters';
import DesignSpecifications from '../../components/DesignSpecifications';
import OperatingValues from '../../components/OperatingValues';
import Charts from '../../components/Charts';
import DetailedSchematic from '../../components/DetailedSchematic';
import useFetchData from '../../hooks/useFetchData';
import tabbarSlice from '../../components/Tabbar/tabbarSlice';

import pushToGTM, { clickDesignPageTabsGA4Tag, pageLoadGA4Tag } from '../../utils/analytics/gtm';
import { useTranslation } from 'react-i18next';
import { localView } from '../../utils/analytics/local';
import { Typography } from '@material-ui/core';

const DesignPage = ({ screenWidth }) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const [activeSubTab, setActiveSubTab] = useState(0);
	const [opValuesCategory, setOpValuesCategory] = useState('SYSTEM_INFO');
	const disabledTabs = useSelector(state => state.tabbar.disabledTabs);
	const fetchDesignData = useSelector(state => state.selectedDesign.fetchDesignData);
	const inputParametersSD = useSelector(state => state.selectedDesign.detail.inputParameters);
	const initialVinNom = useSelector(state => state.selectedDesign.initialInputParameters.vinNom);
	const progressActive = useSelector(state => state.tabbar.progressActive);
	const icPartNumber = useSelector(state => state.selectedDesign.icPartNumber);
	const dispatch = useDispatch();

	const hasError = useFetchData(fetchDesignData, disabledTabs);

	useEffect(() => {
		pushToGTM(pageLoadGA4Tag('DESIGN'));

		if (disabledTabs.Design) {
			if (disabledTabs.Select) {
				history.push(tabRoutes.find(route => route.name === 'Inputs').path);
			} else {
				history.push(tabRoutes.find(route => route.name === 'Select').path);
			}
		} else if (fetchDesignData) {
			setActiveSubTab(0);
		} else {
			dispatch(tabbarSlice.actions.setProgressActive(false));
		}
	}, [dispatch, disabledTabs]);

	const handleTabsClick = (_event, value) => {
		pushToGTM(clickDesignPageTabsGA4Tag(value));

		setActiveSubTab(value);
	};

	if (!progressActive && hasError) {
		return <ErrorPage destinationPage={'Select'} />;
	}

	return (
		<SidepanelLayout
			sidepanel={
				<Grid
					container
					direction={screenWidth === 'sm' ? 'row' : 'column'}
					spacing={isWidthDown('sm', screenWidth) ? 2 : 3}>
					<Grid item container xs={screenWidth === 'sm' ? 6 : false}>
						<InputParameters inputParameters={inputParametersSD} isVinNomSet={initialVinNom !== ''} />
					</Grid>

					<Grid item container xs={screenWidth === 'sm' ? 6 : false}>
						<DesignSpecifications />
					</Grid>
				</Grid>
			}
			content={
				<Fragment>
					<Paper className={classes.tabsPaper}>
						{progressActive ? (
							<div className={classes.tabsSkeletonContainer}>
								<Skeleton width="100%" height={DIMENSIONS.SKELETON_TEXT_HEIGHT} />
							</div>
						) : (
							<Tabs
								value={activeSubTab}
								onChange={handleTabsClick}
								variant="scrollable"
								scrollButtons="auto"
								classes={{
									indicator: classes.tabsIndicator
								}}>
								<Tab
									id="Design__schematic_tab"
									disabled={progressActive}
									classes={{
										root: classes.tabRoot,
										selected: classes.tabSelected
									}}
									label={<Typography>{t('pages.design.tabs.schematic')}</Typography>}
								/>
								<Tab
									id="Design__operatingValues_tab"
									disabled={progressActive}
									classes={{
										root: classes.tabRoot,
										selected: classes.tabSelected
									}}
									label={<Typography>{t('pages.design.tabs.opValues')}</Typography>}
								/>
								<Tab
									id="Design__charts_tab"
									disabled={progressActive}
									classes={{
										root: classes.tabRoot,
										selected: classes.tabSelected
									}}
									label={<Typography>{t('pages.design.tabs.charts')}</Typography>}
								/>
							</Tabs>
						)}
					</Paper>

					<ThemeProvider theme={theme}>
						{activeSubTab === 0 && <DetailedSchematic page={'DESIGN'} />}

						{activeSubTab === 1 && (
							<OperatingValues
								page={'DESIGN'}
								opValuesCategory={opValuesCategory}
								setOpValuesCategory={setOpValuesCategory}
								screenWidth={screenWidth}
								onClick={localView('Design Operating Values', icPartNumber)}
							/>
						)}

						{activeSubTab === 2 && (
							<Charts page={'DESIGN'} screenWidth={screenWidth} onClick={localView('Design Charts', icPartNumber)} />
						)}
					</ThemeProvider>
				</Fragment>
			}
		/>
	);
};

DesignPage.propTypes = {
	screenWidth: PropTypes.number.isRequired
};

export default DesignPage;
